// ABOUTME: The email link's guest session: redeems each `?t=` once (kept in memory, never in storage), a new link token
// ABOUTME: included, and re-redeems when the 60-minute session lapses, the way the guest form provider does.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logError } from 'utils/datadog';
import { createGuestInterviewApi, redeemInterviewLink } from './api';
import {
  INTERVIEW_NOT_FOUND_ERROR_CODE,
  INTERVIEW_SESSION_EXPIRED_ERROR_CODE,
  INTERVIEWS_UNAVAILABLE_ERROR_CODE
} from './constants';
import { interviewErrorCode } from './errors';
import {
  InterviewApi,
  InterviewGuestSession,
  InterviewParticipantRole
} from './types';

/**
 * Where the link landed. `not-found` is an unknown or expired link (pro-core answers both alike), `unavailable` the
 * feature being off, `wrong-role` a host's or attendee's link opened in the talent app, `session-expired` a 401/403
 * on the redeem or on the re-redeem after the session lapsed: the link expired or a newer email replaced it.
 */
export type GuestInterviewState =
  | 'loading'
  | 'active'
  | 'missing-token'
  | 'not-found'
  | 'unavailable'
  | 'wrong-role'
  | 'session-expired'
  | 'error';

/**
 * In memory only, keyed by link token: a remount within one page load reuses the session rather than redeeming
 * again, which would end the first one server-side ("a new redeem ends the previous session").
 */
let liveSession: { linkToken: string; session: InterviewGuestSession } | null =
  null;

export const clearCachedInterviewSession = (): void => {
  liveSession = null;
};

const stateOf = (error: unknown): GuestInterviewState => {
  switch (interviewErrorCode(error)) {
    case INTERVIEW_NOT_FOUND_ERROR_CODE:
      return 'not-found';
    case INTERVIEWS_UNAVAILABLE_ERROR_CODE:
      return 'unavailable';
    case INTERVIEW_SESSION_EXPIRED_ERROR_CODE:
      return 'session-expired';
    default:
      return 'error';
  }
};

export interface GuestInterviewSessionState {
  state: GuestInterviewState;
  interviewRefId: string | null;
  api: InterviewApi | null;
  /** From `session-expired`: forget the cached session and redeem the link once more. */
  reload: () => void;
}

export const useGuestInterviewSession = (
  linkToken: string | null
): GuestInterviewSessionState => {
  const [session, setSession] = useState<InterviewGuestSession | null>(null);
  const [outcome, setOutcome] = useState<GuestInterviewState | null>(null);
  /** The bearer, tagged with the link token it was minted for, so a request never carries another link's session. */
  const tokenRef = useRef<{ linkToken: string; sessionToken: string } | null>(
    null
  );
  /**
   * The link token last acted on. Per token, not per mount: a second email's link opened in the same tab changes `?t=`
   * without a remount and must be redeemed, while StrictMode's double invoke for one token must not mint two sessions.
   * `undefined` until the first run, so a missing token (null) is acted on once as well.
   */
  const attemptedTokenRef = useRef<string | null | undefined>(undefined);
  const recoveringRef = useRef<Promise<string | null> | null>(null);
  /** The link token whose redeem is in flight, so a stale redeem's `finally` never clears a newer link's flag. */
  const redeemingRef = useRef<string | null>(null);

  /**
   * Every async path captures its link token at the start and, on success, failure and in `finally`, touches shared
   * or current state only while that token is still the one the page shows: a late answer for an older link is dropped.
   */
  const isCurrent = useCallback(
    (token: string) => attemptedTokenRef.current === token,
    []
  );

  const adopt = useCallback((token: string, next: InterviewGuestSession) => {
    tokenRef.current = { linkToken: token, sessionToken: next.sessionToken };
    setSession(next);
    if (next.role !== InterviewParticipantRole.TALENT) {
      setOutcome('wrong-role');
    }
  }, []);

  const redeem = useCallback(
    (token: string) => {
      redeemingRef.current = token;
      redeemInterviewLink(token)
        .then(next => {
          if (!isCurrent(token)) return;
          liveSession = { linkToken: token, session: next };
          adopt(token, next);
        })
        .catch((error: unknown) => {
          if (!isCurrent(token)) return;
          const next = stateOf(error);
          if (next === 'error') logError('Interview link redeem failed', error);
          setOutcome(next);
        })
        .finally(() => {
          if (isCurrent(token) && redeemingRef.current === token) {
            redeemingRef.current = null;
          }
        });
    },
    [adopt, isCurrent]
  );

  useEffect(() => {
    // Once per link token: StrictMode's double invoke must not mint two sessions.
    if (attemptedTokenRef.current === linkToken) return;
    attemptedTokenRef.current = linkToken;
    // A new link: the previous one's session, outcome and any recovery in flight belong to that link, not this one.
    tokenRef.current = null;
    recoveringRef.current = null;
    redeemingRef.current = null;
    setSession(null);
    setOutcome(null);
    if (!linkToken) {
      setOutcome('missing-token');
      return;
    }
    if (liveSession?.linkToken === linkToken) {
      adopt(linkToken, liveSession.session);
      return;
    }
    redeem(linkToken);
  }, [linkToken, adopt, redeem]);

  /**
   * Only from a dead end (no live session to end by mistake), and single-flight so a double tap mints one session.
   * A re-redeem in flight has already settled: `session-expired` is set in its catch, after which it is cleared.
   */
  const reload = useCallback(() => {
    const token = linkToken;
    if (!token || !isCurrent(token) || redeemingRef.current === token) return;
    clearCachedInterviewSession();
    tokenRef.current = null;
    setSession(null);
    setOutcome(null);
    redeem(token);
  }, [linkToken, isCurrent, redeem]);

  /** Single-flight: concurrent 401s share one re-redeem. */
  const recoverSession = useCallback((): Promise<string | null> => {
    const token = linkToken;
    if (!token || !isCurrent(token)) return Promise.resolve(null);
    if (recoveringRef.current) return recoveringRef.current;
    const attempt: Promise<string | null> = redeemInterviewLink(token)
      .then(next => {
        if (!isCurrent(token)) return null;
        liveSession = { linkToken: token, session: next };
        tokenRef.current = {
          linkToken: token,
          sessionToken: next.sessionToken
        };
        return next.sessionToken;
      })
      .catch((error: unknown) => {
        if (!isCurrent(token)) return null;
        if (liveSession?.linkToken === token) clearCachedInterviewSession();
        setOutcome(stateOf(error));
        return null;
      })
      .finally(() => {
        // Only this attempt's own flag: a newer link's recovery in flight stays single-flight.
        if (recoveringRef.current === attempt) recoveringRef.current = null;
      });
    recoveringRef.current = attempt;
    return attempt;
  }, [linkToken, isCurrent]);

  /** The current link's session only: an api built for an older link gets no bearer rather than another link's. */
  const getSessionToken = useCallback((): string | null => {
    const current = tokenRef.current;
    if (!linkToken || !isCurrent(linkToken)) return null;
    return current?.linkToken === linkToken ? current.sessionToken : null;
  }, [linkToken, isCurrent]);

  const api = useMemo(
    () =>
      session ? createGuestInterviewApi(getSessionToken, recoverSession) : null,
    [session, getSessionToken, recoverSession]
  );

  let state: GuestInterviewState = 'loading';
  if (outcome) state = outcome;
  else if (session) state = 'active';

  return {
    state,
    interviewRefId: session?.interviewRefId ?? null,
    api,
    reload
  };
};
